import { useEffect } from "react";
import {
  faEdit,
  faTrashAlt,
  faBuilding,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import "../../assets/styles/custom-styles.css";
import MainLayout from "../../layouts/MainLayout";
import { usePermissions } from "../../hooks/usePermissions";
import useUrlFilters from "../../hooks/useUrlFilters";
import useGenericFilter from "../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../components/AutoCompleteFilter";
import baseService from "../../services/baseService";
import useLoader from "../../hooks/useLoader";
import useNotification from "../../hooks/useNotification";
import useBaseService from "../../hooks/services/useBaseService";
import { entities } from "../../constants/entities";
import FilterForm from "../../components/FilterForm";
import PageHeader from "../../components/PageHeader";
import ListHeader from "../../components/ListHeader";
import useAction from "../../hooks/useAction";
import useFilterHandlers from "../../hooks/useFilterHandlers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import TableWithInactiveFilter from "../../components/table/TableWithInactiveFilter";
import { Box } from "@mui/material";
import useQueryWithLoader from "../../hooks/useQueryWithLoader";

const OrganizationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccess } = usePermissions();

  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDel } = useBaseService(navigate);

  // Ajuste: Substituir deleted_at por active
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters({
    name: [],
    active: null, // Alterado de deleted_at para active, null para "todos"
  });

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        name: [],
        active: null, // Alterado de deleted_at para active
      },
      handleFilterApply,
      handleFilterClear,
    );

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const {
    data: organizationsResponseData,
    isLoading: isLoadingOrganizations,
    isFetching: isFetchingOrganizations,
    isError: isErrorOrganizations,
    error: organizationsQueryError,
  } = useQueryWithLoader({
    queryKey: ["organizations", filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(entities.organizations.get, params);
      return response.result || [];
    },
    staleTime: 5 * 60 * 1000,
    select: (data) => {
      return {
        organizations: data.data.map((organization) => ({
          id: organization.id,
          name: organization.name,
          color: organization.color,
          status: organization.active === 1 ? "Ativo" : "Inativo", // Ajuste para refletir active
          isActive: organization.active === 1, // Booleano para controle de visibilidade das ações
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { organizations = [], totalPages = 1 } =
    organizationsResponseData || {};

  useEffect(() => {
    if (!isLoadingOrganizations && organizationsResponseData) {
      hideLoader();
    }
    if (isErrorOrganizations && organizationsQueryError) {
      hideLoader();
      console.error("Erro ao buscar organizações:", organizationsQueryError);
      showNotification(
        "error",
        `Erro ao buscar organizações: ${organizationsQueryError?.message}`,
      );
      navigate("/aluguel/organizacoes", { replace: true });
    }
  }, [
    isErrorOrganizations,
    organizationsResponseData,
    isLoadingOrganizations,
    organizationsQueryError,
    hideLoader,
    showNotification,
    navigate,
  ]);

  useEffect(() => {
    if (location.state?.message) {
      showNotification("error", location.state.message);
    }
  }, [location.state, showNotification]);

  const columns = [
    { key: "name", label: "Nome" },
    { key: "color", label: "Cor" },
    { key: "status", label: "Status" },
  ];

  const inactiveMutation = useMutation({
    mutationFn: async (organizationId) => {
      showLoader();
      const response = await baseServiceDel(
        entities.organizations.delete(organizationId),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({
          queryKey: ["organizations", filters, pagination],
        });
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const activateMutation = inactiveMutation;

  const actions = [
    {
      id: "viewDetails",
      icon: faBuilding,
      title: "Ver detalhes",
      buttonClass: "btn-info",
      permission: "Ver organizações",
      onClick: (organization) =>
        navigate(`/aluguel/organizacoes/detalhes/${organization.id}`),
      show: (organization) =>
        organization.isActive && canAccess("Ver organizações"),
    },
    {
      id: "edit",
      icon: faEdit,
      title: "Editar",
      buttonClass: "btn-primary",
      permission: "Atualizar organizações",
      onClick: (organization) =>
        navigate(`/aluguel/organizacoes/editar/${organization.id}`),
      show: (organization) =>
        organization.isActive && canAccess("Atualizar organizações"),
    },
    {
      id: "delete",
      icon: faTrashAlt,
      title: "Excluir",
      buttonClass: "btn-danger",
      permission: "Excluir organizações",
      onClick: (organization) =>
        showConfirmation(
          organization,
          "Você tem certeza que deseja excluir?",
          async () => {
            try {
              await inactiveMutation.mutateAsync(organization.id);
            } catch (error) {
              console.log("Erro ao excluir organização:", {
                status: error.status,
                message: error.message,
              });
            }
          },
        ),
      show: (organization) =>
        organization.isActive && canAccess("Excluir organizações"), // Mostra se a organização estiver ATIVA
    },
    {
      id: "activate",
      icon: faUndo,
      title: "Restaurar",
      buttonClass: "btn-info",
      permission: "Excluir organizações",
      onClick: (organization) =>
        showConfirmation(
          organization,
          "Você tem certeza que deseja restaurar?",
          async () => {
            try {
              await activateMutation.mutateAsync(organization.id);
            } catch (error) {
              console.log("Erro ao restaurar organização:", {
                status: error.status,
                message: error.message,
              });
            }
          },
        ),
      show: (organization) =>
        !organization.isActive && canAccess("Excluir organizações"), // Mostra se a organização estiver INATIVA
    },
  ];

  const inputsfilters = [
    {
      label: "Número",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase="id"
          model="organization"
          value={localFilterValues.id}
          onChange={(selected) => handleFilterChange("id", selected)}
          placeholder="Filtre as organizações pelo número"
          isMulti
          isClearable
        />
      ),
    },
    {
      label: "Nome",
      component: (
        <AutoCompleteFilter
          service={baseService}
          columnDataBase="name"
          model="organization"
          value={localFilterValues.name}
          onChange={(selected) => handleFilterChange("name", selected)}
          placeholder="Filtre as organizações pelo nome"
          isMulti
          isClearable
        />
      ),
    },
  ];

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Organizações"
        showBackButton={true}
        backUrl="/dashboard"
      />

      <div className="container-fluid p-1">
        <FilterForm
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
          filters={localFilterValues}
        />

        <ListHeader
          title="Lista de organizações"
          buttonText="Nova organização"
          buttonLink="/aluguel/organizacoes/criar"
          canAccess={canAccess}
          permission="Criar organizações"
        />

        <Box sx={{ mt: 2 }}>
          <TableWithInactiveFilter
            data={organizations}
            columns={columns}
            actions={actions}
            totalPages={totalPages}
            currentPage={pagination.page - 1}
            onPageChange={changePage}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPagination({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValues}
            localFilterValues={localFilterValues}
            applyUrlFilters={applyUrlFilters}
            showFilterInactive={false}
            type="default"
            isLoading={isLoadingOrganizations}
            isFetching={isFetchingOrganizations}
          />
        </Box>

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default OrganizationPage;
